import type { LiveConfig, LiveOutcome, LiveQuestion } from './config';

/**
 * The interaction engine.
 *
 * There is one runner and five configurations of it, not five renderers. Everything a format does
 * differently is a flag in `FORMAT_BEHAVIOUR` below, and everything else (ordering, progress,
 * required questions, going back, when an answer is committed) is the same code for all of them.
 * A new format is a row in that table plus, at most, one layout in the view.
 *
 * This module is pure: no DOM, no network, no clock. That is what makes each rule testable on its
 * own rather than only through a mounted widget.
 */

/** What a reader has given for one question. One shape per answer shape the event schema allows. */
export type Answer =
  | { readonly kind: 'choice'; readonly option_ids: readonly string[] }
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'text'; readonly value: string };

/** Answers so far, keyed by question id. */
export type Answers = Readonly<Record<string, Answer | undefined>>;

export interface FormatBehaviour {
  /** Trivia. Say whether the reader was right before moving on. */
  readonly reveal: boolean;
  /** Trivia. Keep a running score and finish on it. */
  readonly score: boolean;
  /** Personality. Accumulate outcome weights and finish on the winning outcome. */
  readonly outcome: boolean;
  /** Poll. Fetch the aggregate and show shares. */
  readonly aggregate: boolean;
  /** Survey and form. Free text goes to the submissions store before the submit event is sent. */
  readonly submission: boolean;
  /** Choosing an option in a single choice question moves straight on. */
  readonly autoAdvance: boolean;
  /** Remember the answer locally so a return visit skips the question. Only sensible for a poll. */
  readonly remember: boolean;
  /**
   * What a free text answer looks like when the publisher did not say.
   *
   * Only a fallback now. A question carrying `multiline` decides for itself, and documents
   * published before that field existed do not carry it, which is what this is for.
   */
  readonly longText: boolean;
}

/**
 * Whether this question wants a paragraph.
 *
 * The publisher's choice wins. Guessing from the format was always a guess about intent, and a
 * form asking "tell us what happened" wants room to write regardless of it being a form.
 */
export function wantsLongText(question: { multiline?: boolean | undefined }, behaviour: FormatBehaviour): boolean {
  return question.multiline ?? behaviour.longText;
}

/**
 * A format nobody taught the widget about behaves like a survey: ask the questions, show no
 * results, thank the reader. That is the one fallback that cannot mislead anybody.
 */
const SURVEY: FormatBehaviour = {
  reveal: false,
  score: false,
  outcome: false,
  aggregate: false,
  submission: true,
  autoAdvance: false,
  remember: false,
  longText: true,
};

export const FORMAT_BEHAVIOUR: Readonly<Record<string, FormatBehaviour>> = {
  poll: {
    reveal: false,
    score: false,
    outcome: false,
    aggregate: true,
    submission: false,
    autoAdvance: true,
    remember: true,
    longText: false,
  },
  trivia_quiz: {
    reveal: true,
    score: true,
    outcome: false,
    aggregate: false,
    submission: false,
    autoAdvance: false,
    remember: false,
    longText: false,
  },
  personality_quiz: {
    reveal: false,
    score: false,
    outcome: true,
    aggregate: false,
    submission: false,
    autoAdvance: true,
    remember: false,
    longText: false,
  },
  survey: SURVEY,
  form: {
    reveal: false,
    score: false,
    outcome: false,
    aggregate: false,
    submission: true,
    autoAdvance: false,
    remember: false,
    longText: false,
  },
};

export function behaviourFor(format: string): FormatBehaviour {
  return FORMAT_BEHAVIOUR[format] ?? SURVEY;
}

export function isChoice(question: LiveQuestion): boolean {
  return question.type === 'single_choice' || question.type === 'multiple_choice';
}

/** Whether the reader has actually given something. Whitespace is not an answer. */
export function hasAnswer(answer: Answer | undefined): boolean {
  if (answer === undefined) return false;
  if (answer.kind === 'choice') return answer.option_ids.length > 0;
  if (answer.kind === 'text') return answer.value.trim().length > 0;
  return Number.isFinite(answer.value);
}

/** A required question blocks progress. An optional one never does. */
export function canLeave(question: LiveQuestion, answer: Answer | undefined): boolean {
  return !question.required || hasAnswer(answer);
}

/** Whether a question can be scored at all, which is what makes it count towards max_score. */
export function isScorable(question: LiveQuestion): boolean {
  return isChoice(question) && question.options.some((option) => option.is_correct === true);
}

/**
 * Right or wrong, for the reader's benefit only. The set chosen has to match the set marked
 * correct exactly, so half of a multiple choice answer does not score.
 */
export function isCorrect(question: LiveQuestion, answer: Answer | undefined): boolean {
  if (!isScorable(question) || answer === undefined || answer.kind !== 'choice') return false;
  const correct = question.options.filter((option) => option.is_correct === true);
  if (correct.length !== answer.option_ids.length) return false;
  return correct.every((option) => answer.option_ids.includes(option.id));
}

export interface Score {
  readonly score: number;
  readonly max_score: number;
}

/**
 * The score shown to the reader. Advisory: the projector recomputes it from the published config,
 * because anything the client asserts can be forged.
 */
export function scoreFor(config: LiveConfig, answers: Answers): Score {
  let score = 0;
  let max = 0;
  for (const question of config.questions) {
    if (!isScorable(question)) continue;
    max += 1;
    if (isCorrect(question, answers[question.id])) score += 1;
  }
  return { score, max_score: max };
}

/**
 * The winning outcome of a personality quiz.
 *
 * Ties are broken by the order the outcomes are declared in: the first declared outcome among
 * those on the highest total wins. That is deterministic (no reliance on object key order or on
 * which question happened to be answered first) and it is a decision the publisher can control,
 * because they choose the order in the editor.
 */
export function pickOutcome(config: LiveConfig, answers: Answers): LiveOutcome | null {
  const outcomes = config.outcomes ?? [];
  if (outcomes.length === 0) return null;

  const totals = new Map<string, number>();
  for (const question of config.questions) {
    const answer = answers[question.id];
    if (answer === undefined || answer.kind !== 'choice') continue;
    for (const option of question.options) {
      if (!answer.option_ids.includes(option.id)) continue;
      const weights = option.outcome_weights;
      if (weights === undefined) continue;
      for (const outcomeId of Object.keys(weights)) {
        const weight = weights[outcomeId];
        if (weight === undefined) continue;
        totals.set(outcomeId, (totals.get(outcomeId) ?? 0) + weight);
      }
    }
  }

  let winner: LiveOutcome | null = null;
  let best = -Infinity;
  for (const outcome of outcomes) {
    const total = totals.get(outcome.id) ?? 0;
    if (total > best) {
      best = total;
      winner = outcome;
    }
  }
  return winner;
}

export interface SubmissionField {
  readonly question_id: string;
  readonly value: string;
}

/**
 * The submissions endpoint's own limit on one value, mirrored here.
 *
 * The field stops the reader at this length so a long answer never becomes a 400 they can do
 * nothing about, and the clamp below is the belt to that pair of braces.
 */
export const MAX_VALUE_CHARS = 1000;

/**
 * The values that must never touch an event.
 *
 * A survey or form answer can be a name, an email or something about somebody's health, so it goes
 * to the submissions store and the event carries only the reference that comes back. Option ids
 * and numbers are safe inline and stay in `answer` events.
 */
export function freeTextFields(config: LiveConfig, answers: Answers): SubmissionField[] {
  const fields: SubmissionField[] = [];
  for (const question of config.questions) {
    if (question.type !== 'free_text') continue;
    const answer = answers[question.id];
    if (answer === undefined || answer.kind !== 'text') continue;
    const value = answer.value.trim().slice(0, MAX_VALUE_CHARS);
    if (value.length === 0) continue;
    fields.push({ question_id: question.id, value });
  }
  return fields;
}

/**
 * Whether a result screen is shown at the end.
 *
 * `show_results: 'never'` is honoured for every format, not only the survey and form where the
 * content model already forces it. A publisher who turns results off on a trivia quiz gets a quiz
 * with no score screen.
 */
export function showsFinalResult(config: LiveConfig): boolean {
  if (config.presentation.show_results === 'never') return false;
  const behaviour = behaviourFor(config.format);
  return behaviour.aggregate || behaviour.score || behaviour.outcome;
}

/** Whether the reader is told there and then whether they were right. */
export function revealsAnswer(config: LiveConfig): boolean {
  return behaviourFor(config.format).reveal && config.presentation.show_results === 'after_answer';
}

/** Toggle for multiple choice, replace for single choice. */
export function chooseOption(
  question: LiveQuestion,
  answer: Answer | undefined,
  optionId: string,
): Answer | null {
  if (!question.options.some((option) => option.id === optionId)) return null;
  if (question.type !== 'multiple_choice') return { kind: 'choice', option_ids: [optionId] };

  const current = answer !== undefined && answer.kind === 'choice' ? answer.option_ids : [];
  const next = current.includes(optionId)
    ? current.filter((id) => id !== optionId)
    : [...current, optionId];
  return { kind: 'choice', option_ids: next };
}
